import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  ItemDefaultValues,
  UpsertItemDefaultValuesParams,
  GetItemDefaultValuesResponse,
  UpsertItemDefaultValuesResponse,
} from "@forgepoint/shared";

interface ItemDefaultValuesState {
  defaults: ItemDefaultValues | null;
  isLoading: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  error: string | null;
  fetchDefaults: () => Promise<void>;
  updateDefaults: (params: UpsertItemDefaultValuesParams) => Promise<boolean>;
  deleteDefaults: () => Promise<boolean>;
}

/** Zustand store for managing organization-level item default values. */
export const useItemDefaultValuesStore = create<ItemDefaultValuesState>()(
  immer((set) => ({
    defaults: null,
    isLoading: false,
    isSaving: false,
    isDeleting: false,
    error: null,

    fetchDefaults: async () => {
      set({ isLoading: true, error: null });

      try {
        const response = await fetchApi("item-default-values", "GET");

        if (!response.ok) {
          throw new Error("Failed to fetch item default values");
        }

        const data = response.data as GetItemDefaultValuesResponse;
        set({ defaults: data.itemDefaultValues ?? null, isLoading: false });
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "An unknown error occurred";
        captureException(err as Error);
        set({ error: errorMsg, isLoading: false });
      }
    },

    updateDefaults: async (params) => {
      set({ isSaving: true, error: null });

      try {
        const response = await fetchApi("item-default-values", "PUT", {
          body: params,
        });

        if (!response.ok) {
          throw new Error("Failed to update item default values");
        }

        const data = response.data as UpsertItemDefaultValuesResponse;
        set({ defaults: data.itemDefaultValues, isSaving: false });
        return true;
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "An unknown error occurred";
        captureException(err as Error);
        set({ error: errorMsg, isSaving: false });
        return false;
      }
    },

    deleteDefaults: async () => {
      set({ isDeleting: true, error: null });

      try {
        const response = await fetchApi("item-default-values", "DELETE");

        if (!response.ok) {
          throw new Error("Failed to delete item default values");
        }

        set({ defaults: null, isDeleting: false });
        return true;
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "An unknown error occurred";
        captureException(err as Error);
        set({ error: errorMsg, isDeleting: false });
        return false;
      }
    },
  })),
);
